/**
 * Base Entity Service - Abstract class for all entity services
 */

import type { Knex } from 'knex'
import type {
  EntityDefinition,
  PaginatedResult,
  ModuleContext,
  EntityServiceHooks,
  ActionDefinition
} from '@gzl10/nexus-sdk'
import { resolveLocalized } from '@gzl10/nexus-sdk'
import type { EntityQuery, ServerEntityQuery, EntityService } from '@kernel/runtime/index.js'
import type { LoggerService } from '@kernel/logger/types.js'
import {
  excludeSensitiveFields as excludeSensitive,
  excludeSensitiveFieldsFromArray as excludeSensitiveArray
} from '../helpers/index.js'
import {
  applyFilters as applyFiltersHelper,
  applyInMemoryFilters as applyInMemoryFiltersHelper
} from '@kernel/db/filter-helpers.js'
import type {
  EntityRepository,
  ICollectionRepository,
  IViewRepository
} from '../repositories/types.js'
import { createRepoFromCtx } from '../repositories/factory.js'

/**
 * Abstract base class for entity services
 *
 * Provides common functionality:
 * - Access to ModuleContext (db, logger, etc.)
 * - Entity definition
 * - Default pagination
 * - Hook system for lifecycle events (internos + externos via createEntityService)
 */
export abstract class BaseEntityService<T = unknown> implements EntityService<T> {
  protected readonly db: Knex
  protected readonly logger: ModuleContext['core']['logger']
  protected readonly generateId: () => string
  protected readonly generateIdByType: (type?: 'ulid' | 'uuid' | 'nanoid' | 'cuid2' | 'auto' | 'custom') => string | undefined
  protected readonly errors: ModuleContext['core']['errors']
  /** Module name for entity event emission (stamped by registry) */
  protected readonly moduleName: string
  /** Stable entity identifier for real-time rooms and events (table, key, or label) */
  protected readonly entityKey: string
  /** Hooks externos inyectados via createEntityService */
  protected readonly externalHooks?: EntityServiceHooks<T>
  /** Temporary storage for delete actor ID (used by audit event emission) */
  protected _deleteActorId?: string
  /**
   * Repository for CRUD operations.
   * If not provided in the constructor, built lazily via buildRepo() from ctx + definition.
   * Note: single/computed/external entity types may not have a valid repo; for those
   * the repo is constructed only if accessed and the type is supported by the factory.
   */
  protected readonly repo: EntityRepository

  constructor(
    protected readonly ctx: ModuleContext,
    public readonly definition: EntityDefinition,
    hooks?: EntityServiceHooks<T>,
    repo?: EntityRepository
  ) {
    const adapterName = (definition as unknown as { adapter?: string }).adapter
    this.db = ctx.db.getKnex(adapterName)
    this.logger = ctx.core.logger
    this.generateId = ctx.core.generateId
    this.generateIdByType = ctx.core.generateIdByType
    this.errors = ctx.core.errors
    this.moduleName = (definition as unknown as { _moduleName?: string })._moduleName ?? 'unknown'
    const def = definition as Record<string, unknown>
    this.entityKey = (def['table'] as string) ?? (def['key'] as string) ?? resolveLocalized(definition.label, 'en').toLowerCase().replace(/\s+/g, '_')
    this.externalHooks = hooks
    this.repo = repo ?? this.buildRepo()
  }

  /**
   * Build default repository from context + definition.
   * Subclasses can override to provide custom logic if needed.
   * Returns a stub repo for entity types not supported by the factory
   * (single, computed, external) so this.repo is always assignable.
   */
  protected buildRepo(): EntityRepository {
    try {
      return createRepoFromCtx(this.ctx, this.definition)
    } catch {
      // Stub repo for non-table-backed entities (single without table, computed, external).
      // Subclasses that need a working repo provide one explicitly.
      return {
        findAll: async () => ({ items: [], total: 0, page: 1, limit: 20, totalPages: 0, hasNext: false }),
        findById: async () => null,
        count: async () => 0,
      } as unknown as EntityRepository
    }
  }

  /**
   * Get the ID type configured for this entity.
   * @returns ID type from definition or 'ulid' as default
   */
  protected getIdType(): 'ulid' | 'uuid' | 'nanoid' | 'cuid2' | 'auto' | 'custom' | 'pattern' {
    const idField = this.definition.fields?.['id']
    return (idField?.db?.idType as 'ulid' | 'uuid' | 'nanoid' | 'cuid2' | 'auto' | 'custom' | 'pattern') ?? 'ulid'
  }

  /**
   * Get the pattern configuration for this entity (if using pattern IDs).
   */
  protected getPatternConfig(): { pattern: string; prefix?: string; suffix?: string; resetOn?: 'never' | 'year' | 'month' | 'day'; startAt?: number } | undefined {
    const idField = this.definition.fields?.['id']
    return idField?.db?.patternConfig
  }

  /**
   * Resolve the ID for a new entity (async version).
   * - If user provides ID, use it (for all types)
   * - For 'auto': return undefined (DB generates)
   * - For 'custom' without user ID: throw error
   * - For 'pattern': generate using sequence
   * - For other types: generate ID
   *
   * @param providedId - ID provided by user (optional)
   * @returns Resolved ID or undefined for auto-increment
   */
  protected async resolveEntityId(providedId?: unknown): Promise<string | undefined> {
    // User provided ID takes precedence
    if (providedId !== undefined && providedId !== null) {
      return String(providedId)
    }

    const idType = this.getIdType()

    // Auto-increment: let DB handle it
    if (idType === 'auto') {
      return undefined
    }

    // Custom: user must provide ID
    if (idType === 'custom') {
      throw new this.errors.ValidationError('ID is required for custom ID type')
    }

    // Pattern: generate using sequence
    if (idType === 'pattern') {
      const patternConfig = this.getPatternConfig()
      if (!patternConfig) {
        throw new this.errors.ValidationError('Pattern configuration is required for pattern ID type')
      }
      // Import dynamically to avoid circular dependency
      const { generatePatternId } = await import('../../../core/utils/sequence.js')
      return generatePatternId(this.db, this.table, patternConfig)
    }

    // Generate ID for other types
    return this.generateIdByType(idType)
  }

  /**
   * Get table name for persistent entities
   */
  protected get table(): string {
    if ('table' in this.definition && this.definition.table) {
      return this.definition.table
    }
    throw new Error(`Entity ${resolveLocalized(this.definition.label, 'en')} has no table`)
  }

  protected get loggerService(): LoggerService | undefined {
    return this.ctx.services.getOptional<LoggerService>('logger')
  }

  /**
   * Get all entities with pagination
   */
  abstract findAll(query?: EntityQuery): Promise<PaginatedResult<T>>

  /**
   * Get entity by ID
   */
  abstract findById(id: string): Promise<T | null>

  // ============================================================================
  // Count
  // ============================================================================

  /**
   * Count entities matching optional filters.
   * Available for all persistent entity types.
   * Subclasses can override for soft delete or adapter support.
   */
  async count(filters?: Record<string, unknown>): Promise<number> {
    const typeFilters = this.getTypeFilters()
    const merged = { ...typeFilters, ...(filters ?? {}) }
    const repo = this.repo as ICollectionRepository | IViewRepository
    return repo.count(Object.keys(merged).length > 0 ? merged : undefined)
  }

  // ============================================================================
  // Template Method for findAll
  // ============================================================================

  /**
   * Template method for findAll with customizable hooks.
   * Subclasses can override hooks instead of reimplementing the whole method.
   *
   * Hooks:
   * - applyTypeFilters(qb): Add type-specific filters (softDelete, TTL, etc.)
   * - getDefaultSort(): Return default sort config { field, order }
   * - afterFindAll(items): Post-process items before returning
   */
  protected async baseFindAll(query?: EntityQuery): Promise<PaginatedResult<T>> {
    const typeFilters = this.getTypeFilters()
    const mergedFilters = Object.keys(typeFilters).length > 0
      ? { ...typeFilters, ...(query?.filters ?? {}) }
      : query?.filters

    const mergedQuery: EntityQuery = {
      ...query,
      filters: mergedFilters,
    }

    const repo = this.repo as ICollectionRepository | IViewRepository
    const rawResult = await repo.findAll(mergedQuery)

    const items = this.parseJsonFieldsFromArray(rawResult.items as Record<string, unknown>[]) as T[]
    const processedItems = await this.afterFindAll(items)

    let result = this.buildPaginatedResult(processedItems, rawResult.total, rawResult.page, rawResult.limit)
    if (this.externalHooks?.afterFindAll) {
      result = await this.externalHooks.afterFindAll(result)
    }
    return result
  }

  /**
   * Type-specific filters (soft-delete, TTL, retention, etc.) merged into all CRUD operations.
   * Subclasses override to inject filters that apply to findAll/findById/count/delete.
   * Default: no filters.
   *
   * Example (soft-delete):
   *   protected override getTypeFilters() { return { deleted_at: null } }
   */
  protected getTypeFilters(): Record<string, unknown> {
    return {}
  }

  /**
   * @deprecated Use getTypeFilters() instead. Kept for backward compatibility but
   * no longer invoked by baseFindAll/baseFindById/count/baseDelete.
   * Subclasses that still override this should migrate to getTypeFilters().
   */
  protected applyTypeFilters(qb: Knex.QueryBuilder): Knex.QueryBuilder {
    return qb
  }

  /**
   * Get default sort configuration for this entity type.
   * Uses definition.defaultSort if defined, otherwise null.
   * Override in subclasses for type-specific defaults (e.g., events = created_at desc).
   */
  protected getDefaultSort(): { field: string; order: 'asc' | 'desc' } | null {
    return (this.definition as { defaultSort?: { field: string; order: 'asc' | 'desc' } }).defaultSort ?? null
  }

  /**
   * Apply sorting with type-specific defaults.
   */
  protected applySortingWithDefaults(qb: Knex.QueryBuilder, query?: EntityQuery): Knex.QueryBuilder {
    if (query?.sort) {
      return qb.orderBy(query.sort, query.order ?? 'asc')
    }

    const defaultSort = this.getDefaultSort()
    if (defaultSort) {
      return qb.orderBy(defaultSort.field, defaultSort.order)
    }

    // Fall back to base applySorting (uses labelField)
    return this.applySorting(qb, query)
  }

  /**
   * Post-process items after query.
   * Override in subclasses for cleanup tasks, etc.
   */
  protected async afterFindAll(items: T[]): Promise<T[]> {
    return items
  }

  /**
   * Standard findById implementation for DB-backed entities.
   * Subclasses can override findById to add custom behavior (e.g., soft delete filter, default merge).
   */
  protected async baseFindById(id: string): Promise<T | null> {
    const typeFilters = this.getTypeFilters()
    const repo = this.repo as ICollectionRepository | IViewRepository

    let entity: Record<string, unknown> | null = null
    if (Object.keys(typeFilters).length > 0) {
      // Type filters present (e.g. soft-delete) — round-trip via findAll so they apply.
      const result = await repo.findAll({ filters: { ...typeFilters, id }, limit: 1 })
      entity = (result.items[0] as Record<string, unknown> | undefined) ?? null
    } else {
      entity = (await repo.findById(id)) as Record<string, unknown> | null
    }

    if (!entity) return null
    const parsed = this.parseJsonFields(entity)
    return this.processAfterFindById(parsed as T)
  }

  /**
   * In-memory findAll for services without DB tables (computed, virtual).
   * Applies filters, search, sort, and pagination to a raw array.
   */
  protected inMemoryFindAll(
    items: unknown[],
    query?: EntityQuery
  ): PaginatedResult<T> {
    const { page, limit, offset } = this.getPagination(query)
    let result = [...items]

    // Apply in-memory filters
    if (query?.filters) {
      result = this.applyInMemoryFilters(result, query.filters)
    }

    // Apply search by labelField
    const labelField = (this.definition as { labelField?: string }).labelField
    if (query?.search && labelField) {
      const searchLower = query.search.toLowerCase()
      result = result.filter(item => {
        const value = (item as Record<string, unknown>)[labelField]
        return value != null && String(value).toLowerCase().includes(searchLower)
      })
    }

    // Apply sorting (query takes precedence, then definition.defaultSort)
    const sortField = query?.sort ?? this.getDefaultSort()?.field
    const sortOrder = query?.sort ? (query.order ?? 'asc') : (this.getDefaultSort()?.order ?? 'asc')
    if (sortField) {
      result.sort((a, b) => {
        const aVal = (a as Record<string, unknown>)[sortField]
        const bVal = (b as Record<string, unknown>)[sortField]
        if (String(aVal) < String(bVal)) return sortOrder === 'asc' ? -1 : 1
        if (String(aVal) > String(bVal)) return sortOrder === 'asc' ? 1 : -1
        return 0
      })
    }

    // Paginate (limit=0 means unpaginated)
    const total = result.length
    const paginatedItems = limit === 0 ? result as T[] : result.slice(offset, offset + limit) as T[]

    return this.buildPaginatedResult(paginatedItems, total, page, limit)
  }

  /**
   * Get current timestamp formatted for the database.
   * Avoids importing schema-helpers at module level (circular dependency).
   */
  protected getNowTimestamp(): string {
    const client = (this.db as unknown as { client: { config: { client: string } } }).client?.config?.client ?? ''
    if (client === 'mysql' || client === 'mysql2') {
      return new Date().toISOString().slice(0, 19).replace('T', ' ')
    }
    return new Date().toISOString()
  }

  /**
   * Standard hard-delete implementation for DB-backed entities.
   */
  protected async baseDelete(id: string): Promise<void> {
    const existing = await this.findById(id)
    if (!existing) {
      throw new this.errors.NotFoundError(resolveLocalized(this.definition.label, 'en'))
    }

    await this.beforeDelete(id)
    const repo = this.repo as ICollectionRepository
    await repo.delete(id)
    await this.afterDelete(id)
  }

  /**
   * Process a record after findById.
   * Calls external afterFindById hook if defined.
   */
  protected async processAfterFindById(record: T | null): Promise<T | null> {
    if (record && this.externalHooks?.afterFindById) {
      return await this.externalHooks.afterFindById(record)
    }
    return record
  }

  // ============================================================================
  // Hooks - Llaman primero a hooks externos, luego internos (subclases)
  // ============================================================================

  /**
   * Called before creating an entity.
   * Runs the external hook (if any) and then allows subclass override.
   * @returns Modified data
   */
  protected async beforeCreate(data: Partial<T>): Promise<Partial<T>> {
    let result = data
    if (this.externalHooks?.beforeCreate) {
      result = await this.externalHooks.beforeCreate(result)
    }
    return this.internalBeforeCreate(result)
  }

  /** Override in subclasses for custom beforeCreate logic */
  protected async internalBeforeCreate(data: Partial<T>): Promise<Partial<T>> {
    return this.sanitizeForeignKeys(data)
  }

  /**
   * Convert empty strings to null for foreign key fields.
   * Prevents FK constraint violations when selects are cleared.
   */
  protected sanitizeForeignKeys(data: Partial<T>): Partial<T> {
    const result = { ...data } as Record<string, unknown>

    for (const [key, field] of Object.entries(this.definition.fields ?? {})) {
      // Fields with relation or dynamic options (relation selects)
      const hasRelation = 'relation' in field && field.relation !== undefined
      const isDynamicSelect = field.options?.endpoint !== undefined

      if ((hasRelation || isDynamicSelect) && result[key] === '') {
        result[key] = null
      }
    }

    return result as Partial<T>
  }

  /**
   * Called after creating an entity
   */
  protected async afterCreate(entity: T): Promise<void> {
    await this.internalAfterCreate(entity)
    if (this.externalHooks?.afterCreate) {
      await this.externalHooks.afterCreate(entity)
    }
  }

  /** Override in subclasses for custom afterCreate logic */
  protected async internalAfterCreate(entity: T): Promise<void> {
    this.emitEntityEvent('created', entity)
    this.emitAuditEvent('record.created', entity)
  }

  /**
   * Called before updating an entity
   * @returns Modified data
   */
  protected async beforeUpdate(id: string, data: Partial<T>): Promise<Partial<T>> {
    let result = data
    if (this.externalHooks?.beforeUpdate) {
      result = await this.externalHooks.beforeUpdate(id, result)
    }
    return this.internalBeforeUpdate(id, result)
  }

  /** Override in subclasses for custom beforeUpdate logic */
  protected async internalBeforeUpdate(_id: string, data: Partial<T>): Promise<Partial<T>> {
    return this.sanitizeForeignKeys(data)
  }

  /**
   * Called after updating an entity
   */
  protected async afterUpdate(entity: T): Promise<void> {
    await this.internalAfterUpdate(entity)
    if (this.externalHooks?.afterUpdate) {
      await this.externalHooks.afterUpdate(entity)
    }
  }

  /** Override in subclasses for custom afterUpdate logic */
  protected async internalAfterUpdate(entity: T): Promise<void> {
    this.emitEntityEvent('updated', entity)
    this.emitAuditEvent('record.updated', entity)
  }

  /**
   * Called before deleting an entity
   */
  protected async beforeDelete(id: string): Promise<void> {
    if (this.externalHooks?.beforeDelete) {
      await this.externalHooks.beforeDelete(id)
    }
    await this.internalBeforeDelete(id)
  }

  /** Override in subclasses for custom beforeDelete logic */
  protected async internalBeforeDelete(_id: string): Promise<void> {
    // Override in subclasses
  }

  /**
   * Called after deleting an entity
   */
  protected async afterDelete(id: string): Promise<void> {
    await this.internalAfterDelete(id)
    if (this.externalHooks?.afterDelete) {
      await this.externalHooks.afterDelete(id)
    }
  }

  /** Override in subclasses for custom afterDelete logic */
  protected async internalAfterDelete(id: string): Promise<void> {
    this.emitEntityEvent('deleted', undefined, id)
    this.emitAuditEvent('record.deleted', undefined, id, this._deleteActorId)
  }

  // ============================================================================
  // Real-time event emission
  // ============================================================================

  /**
   * Emit entity change event via ctx.core.events.
   *
   * Always emits for cross-module listeners (taxonomy cleanup, attachment cleanup, etc.).
   * The event bridge routes these to Socket.IO rooms only when realtime mode is enabled.
   * Fire-and-forget: does not block the HTTP response.
   */
  protected emitEntityEvent(action: 'created' | 'updated' | 'deleted', entity?: T, id?: string): void {
    const recordId = id ?? (entity as Record<string, unknown>)?.['id'] as string
    if (!recordId) {
      return
    }

    this.ctx.core.logger.debug({ action, recordId, module: this.moduleName, entity: this.entityKey }, 'Emitting entity event')
    this.ctx.core.events.emit(`entity.${action}`, {
      tenantId: this.ctx.tenantId,
      module: this.moduleName,
      entity: this.entityKey,
      action,
      id: recordId,
      data: action !== 'deleted' ? (entity as Record<string, unknown>) : undefined,
      userId: (entity as Record<string, unknown>)?.['_authUserId'] as string | undefined,
      timestamp: new Date().toISOString()
    })
  }

  /**
   * Emit audit.log event for entities with audit: true.
   * Fire-and-forget via event bus — audit module picks it up if loaded.
   */
  protected emitAuditEvent(action: 'record.created' | 'record.updated' | 'record.deleted', entity?: T, id?: string, actorId?: string): void {
    const def = this.definition as Record<string, unknown>
    if (!def['audit']) return

    const recordId = id ?? (entity as Record<string, unknown>)?.['id'] as string
    if (!recordId) return

    const resolvedActorId = actorId
      ?? (entity as Record<string, unknown>)?.['created_by'] as string | undefined
      ?? (entity as Record<string, unknown>)?.['updated_by'] as string | undefined

    this.ctx.events.notify('audit.log', {
      source: `entity:${this.moduleName}.${this.entityKey}`,
      action,
      actorId: resolvedActorId,
      resourceType: this.entityKey,
      resourceId: recordId
    })
  }

  // ============================================================================
  // Helpers
  // ============================================================================

  /**
   * Build paginated result from items and count
   */
  protected buildPaginatedResult(
    items: T[],
    total: number,
    page: number,
    limit: number
  ): PaginatedResult<T> {
    // limit=0 means unpaginated — single page with all items
    const totalPages = limit === 0 ? 1 : Math.ceil(total / limit)
    return {
      items,
      total,
      page: limit === 0 ? 1 : page,
      limit: limit === 0 ? total : limit,
      totalPages,
      hasNext: limit === 0 ? false : page < totalPages
    }
  }

  /**
   * Get pagination params with defaults
   */
  protected getPagination(query?: ServerEntityQuery): { page: number; limit: number; offset: number } {
    const maxLimit = query?.maxLimit ?? 100
    const page = Math.max(1, query?.page ?? 1)
    // limit=0 means unpaginated (return all items)
    const limit = query?.limit === 0 ? 0 : Math.min(maxLimit, Math.max(1, query?.limit ?? 20))
    const offset = limit === 0 ? 0 : (page - 1) * limit
    return { page, limit, offset }
  }

  /**
   * Apply sorting to query builder
   */
  protected applySorting(qb: Knex.QueryBuilder, query?: EntityQuery): Knex.QueryBuilder {
    if (query?.sort) {
      const order = query.order ?? 'asc'
      qb.orderBy(query.sort, order)
    } else if ('labelField' in this.definition && this.definition.labelField) {
      // Default sort by label field
      qb.orderBy(this.definition.labelField as string, 'asc')
    }
    return qb
  }

  /**
   * Apply search filter to query builder
   * Override in subclasses for entity-specific search
   */
  protected applySearch(qb: Knex.QueryBuilder, search: string): Knex.QueryBuilder {
    const searchTerm = `%${search}%`
    const searchableFields: string[] = []

    // Collect fields marked searchable
    const fields = 'fields' in this.definition ? this.definition.fields : {}
    for (const [name, field] of Object.entries(fields)) {
      if (field.meta?.searchable) {
        searchableFields.push(name)
      }
    }

    // Add labelField if not already in searchable
    if ('labelField' in this.definition && this.definition.labelField) {
      const lf = this.definition.labelField as string
      if (!searchableFields.includes(lf)) {
        searchableFields.unshift(lf)
      }
    }

    if (searchableFields.length === 0) return qb

    // JSON/localized fields need cast to text for LIKE
    qb.where(function () {
      for (const fieldName of searchableFields) {
        const field = fields[fieldName]
        if (field?.db?.type === 'json') {
          this.orWhereRaw(`CAST(?? AS TEXT) LIKE ?`, [fieldName, searchTerm])
        } else {
          this.orWhere(fieldName, 'like', searchTerm)
        }
      }
    })

    return qb
  }

  /**
   * Apply filters to query builder
   * Supports operators: $eq, $ne, $gt, $gte, $lt, $lte, $contains, $startswith, $endswith, $in, $nin, $isnull
   *
   * @example
   * // Simple equality
   * { status: 'active' }
   *
   * // With operators
   * { name: { $contains: 'john' } }
   * { age: { $gte: 18, $lt: 65 } }
   *
   * // Array shorthand for $in
   * { status: ['active', 'pending'] }
   */
  protected applyFilters(qb: Knex.QueryBuilder, filters: Record<string, unknown>): Knex.QueryBuilder {
    return applyFiltersHelper(qb, filters)
  }

  // ============================================================================
  // In-Memory Filters (for computed/virtual entities)
  // ============================================================================

  /**
   * Apply filters in memory (for non-persistent entities)
   * Supports same operators as Knex: $eq, $ne, $gt, $gte, $lt, $lte, $contains, $startswith, $endswith, $in, $nin, $isnull
   */
  protected applyInMemoryFilters(items: unknown[], filters: Record<string, unknown>): unknown[] {
    return applyInMemoryFiltersHelper(items, filters)
  }

  // ============================================================================
  // CASL Helpers
  // ============================================================================

  /**
   * Get sensitive fields from CASL config.
   * Only available for EntityCaslConfig, not simplified action CASL.
   */
  protected get sensitiveFields(): string[] {
    const casl = this.definition.casl
    if (!casl) return []
    return 'sensitiveFields' in casl ? (casl.sensitiveFields ?? []) : []
  }

  /**
   * Exclude sensitive fields from entity data.
   * Uses definition.casl.sensitiveFields if configured.
   */
  protected excludeSensitiveFields<D extends Record<string, unknown>>(data: D): Partial<D> {
    return excludeSensitive(data, this.sensitiveFields)
  }

  /**
   * Exclude sensitive fields from array of entities.
   */
  protected excludeSensitiveFieldsFromArray<D extends Record<string, unknown>>(items: D[]): Partial<D>[] {
    return excludeSensitiveArray(items, this.sensitiveFields)
  }

  // ============================================================================
  // Field Type Parsing (JSON, Boolean)
  // ============================================================================

  /**
   * Parse fields in an entity based on field definitions.
   * - Converts JSON strings to objects for fields with db.type = 'json'
   * - Converts numeric 0/1 to native booleans for fields with db.type = 'boolean'
   *
   * SQLite and MySQL store booleans as integers (0/1), so we need to convert them.
   */
  protected parseJsonFields<E extends Record<string, unknown>>(entity: E): E {
    if (!entity || typeof entity !== 'object') return entity

    const fields = 'fields' in this.definition ? (this.definition.fields ?? {}) : {}
    const result: Record<string, unknown> = { ...entity }

    for (const [fieldName, fieldDef] of Object.entries(fields)) {
      const value = result[fieldName]

      // JSON: parse string to object
      if (fieldDef.db?.type === 'json' && typeof value === 'string') {
        try {
          result[fieldName] = JSON.parse(value)
        } catch {
          this.logger.warn({ field: fieldName, entity: this.table }, 'Failed to parse JSON field')
        }
      }

      // Boolean: convert 0/1 to true/false (SQLite/MySQL store booleans as integers)
      if (fieldDef.db?.type === 'boolean' && typeof value === 'number') {
        result[fieldName] = value !== 0
      }
    }

    return result as E
  }

  /**
   * Parse fields in array of entities
   */
  protected parseJsonFieldsFromArray<E extends Record<string, unknown>>(items: E[]): E[] {
    return items.map(item => this.parseJsonFields(item))
  }

  /**
   * Serialize entity data for DB insertion/update.
   * - Converts objects/arrays to JSON strings for fields with db.type = 'json'
   *   (all engines: pg driver does NOT auto-serialize plain objects — calls .toString() → [object Object])
   * - Converts booleans to 0/1 for SQLite
   */
  protected serializeForDb<E extends Record<string, unknown>>(data: E): E {
    if (!data || typeof data !== 'object') return data

    const fields = 'fields' in this.definition ? (this.definition.fields ?? {}) : {}
    const result: Record<string, unknown> = { ...data }
    const client = (this.db as unknown as { client: { config: { client: string } } }).client?.config?.client ?? ''
    const isSQLite = client === 'better-sqlite3' || client === 'sqlite3'

    for (const [fieldName, fieldDef] of Object.entries(fields)) {
      const value = result[fieldName]
      if (value === undefined) continue

      // JSON: stringify objects/arrays for ALL engines
      // PG json columns accept valid JSON strings; pg driver does NOT auto-serialize plain JS objects
      if (fieldDef.db?.type === 'json' && value !== null && typeof value === 'object') {
        result[fieldName] = JSON.stringify(value)
      }

      // Boolean: convert to 0/1 for SQLite
      if (fieldDef.db?.type === 'boolean' && isSQLite && typeof value === 'boolean') {
        result[fieldName] = value ? 1 : 0
      }
    }

    return result as E
  }

  // ============================================================================
  // Action Execution
  // ============================================================================

  /**
   * Execute an action defined in this entity programmatically
   *
   * Allows invoking EntityActions from other services without HTTP context.
   * NOTE: Does NOT verify CASL permissions - caller is responsible for authorization.
   *
   * @param actionKey - Key of the action to execute
   * @param input - Input for the handler (without _record if recordId is provided)
   * @param recordId - ID of the record (optional, loads automatically)
   * @param options - Additional options
   * @returns Result from the action handler
   *
   * @example
   * // From another service
   * const storageService = ctx.services['storage_files'] as CollectionService
   * const result = await storageService.executeAction('thumbnail', { width: 200 }, fileId)
   */
  async executeAction(
    actionKey: string,
    input: Record<string, unknown> = {},
    recordId?: string,
    options?: { userId?: string; skipValidation?: boolean }
  ): Promise<unknown> {
    // 1. Find action in definition.actions
    const actions = (this.definition as { actions?: ActionDefinition[] }).actions
    if (!actions?.length) {
      throw new this.errors.NotFoundError('Entity has no actions')
    }

    const action = actions.find(a => a.key === actionKey)
    if (!action) {
      throw new this.errors.NotFoundError(`Action "${actionKey}" not found`)
    }

    // 2. Load record if recordId provided
    let record: Record<string, unknown> | undefined = input['_record'] as Record<string, unknown>
    if (recordId && !record) {
      // LIMITATION: ICollectionRepository.findById doesn't accept projection (action.select),
      // so we read directly via Knex here. Repo refactor doesn't cover this edge case.
      let query = this.db(this.table).where('id', recordId)
      if (action.select?.length) {
        query = query.select(action.select)
      }
      record = await query.first()
      if (!record) {
        throw new this.errors.NotFoundError(resolveLocalized(this.definition.label, 'en'))
      }
    }

    // 3. Validate input if schema exists and not skipped
    let validatedInput = input
    if (action.inputSchema && !options?.skipValidation) {
      validatedInput = action.inputSchema.parse(input) as Record<string, unknown>
    }

    // 4. Extend input with _record and _authUserId
    const extendedInput: Record<string, unknown> = { ...validatedInput }
    if (record) {
      extendedInput['_record'] = record
    }
    if (options?.userId) {
      extendedInput['_authUserId'] = options.userId
    }

    // 5. Execute handler (without req/res)
    if (!action.handler) {
      throw new this.errors.AppError(`No handler defined for action ${actionKey}`, 500)
    }
    return action.handler(this.ctx, extendedInput)
  }
}
